import type { Frame } from "@/api/spaces";
import {
  putFloatingFrame,
  useFloatingFrameStore,
} from "@/desktop/floating-frame-store";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { applyFramePatch } from "@/lib/frame-ingest";

export type ConversationFramePatch = Pick<
  Partial<Frame>,
  "title" | "content" | "status" | "task_id"
> & { metadata: Frame["metadata"] };

/** The local frame a binding starts from when the canvas has no node for it:
 *  a floating window or the workspace overlay renders its own copy. */
export function conversationFrameCopy(
  frameId: string,
): Pick<Frame, "title" | "metadata"> | null {
  const overlay = useWorkspaceConversationOverlay.getState().overlay;
  if (overlay?.frame.id === frameId) return overlay.frame;
  const floating = useFloatingFrameStore.getState().frames[frameId]?.frame;
  return floating
    ? { title: floating.title, metadata: floating.metadata }
    : null;
}

/** Apply one patch to every copy of a conversation frame — canvas node,
 *  floating window and workspace overlay — so each renders the same chat. */
export function patchConversationFrameCopies(
  frameId: string,
  patch: ConversationFramePatch,
): void {
  applyFramePatch(frameId, patch);
  const floating = useFloatingFrameStore.getState().frames[frameId];
  if (floating) {
    putFloatingFrame({
      ...floating,
      frame: {
        ...floating.frame,
        ...(patch.title !== undefined ? { title: patch.title } : {}),
        ...(patch.content !== undefined ? { content: patch.content } : {}),
        ...(patch.status !== undefined ? { status: patch.status } : {}),
        ...(patch.task_id === null ? { taskTicket: null } : {}),
        metadata: patch.metadata,
      },
    });
  }
  const overlay = useWorkspaceConversationOverlay.getState().overlay;
  if (overlay?.frame.id === frameId) {
    useWorkspaceConversationOverlay.getState().open({
      ...overlay,
      frame: { ...overlay.frame, ...patch },
    });
  }
}
